/**
 * ContactSelect
 * 
 * Componente de seleção de contato com busca e dropdown.
 * Permite pesquisar, selecionar e adicionar novos contatos.
 * 
 * Princípios aplicados:
 * - Single Responsibility: Apenas seleção de contato
 * - Componentização: UI isolada com estado local
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import SearchInput from '../../../components/ui/SearchInput';
import { Contact } from '../../../types';
import { PlusIcon } from '../../../components/icons';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { supabase } from '../../../lib/supabase';

/** Evita que caracteres especiais quebrem o padrão `ilike`. */
function sanitizeContactSearchTerm(raw: string): string {
    return raw.trim().replace(/[%_\\]/g, '').slice(0, 80);
}

interface ContactSelectProps {
    contacts: Contact[];
    selectedContactId: string;
    onSelect: (contact: Contact) => void;
    onClear: () => void;
    onAddContact: () => void;
    /** Contato recém-criado ainda não refletido na lista principal */
    pinnedContact?: Contact | null;
}

const ContactSelect: React.FC<ContactSelectProps> = ({
    contacts,
    selectedContactId,
    onSelect,
    onClear,
    onAddContact,
    pinnedContact
}) => {
    const { currentOrganization } = useOrganization();
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [remoteContacts, setRemoteContacts] = useState<Contact[]>([]);
    const [remoteLoading, setRemoteLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const mergedContacts = useMemo(() => {
        const byId = new Map<string, Contact>();
        for (const c of contacts) byId.set(c.id, c);
        if (pinnedContact?.id) byId.set(pinnedContact.id, pinnedContact);
        for (const c of remoteContacts) byId.set(c.id, c);
        return Array.from(byId.values());
    }, [contacts, pinnedContact, remoteContacts]);

    // Sincroniza o campo com o contato selecionado (quando houver).
    // NUNCA limpar `search` aqui quando não há seleção: `mergedContacts` muda a cada busca remota
    // e apagaria o que o usuário está digitando (efeito de “piscar”).
    useEffect(() => {
        if (!selectedContactId) return;
        const contact = mergedContacts.find(c => c.id === selectedContactId);
        if (contact) {
            setSearch(contact.name);
        }
    }, [selectedContactId, mergedContacts]);

    // Limpa o campo só quando a seleção é removida (transição id → vazio), não em todo update da lista.
    useEffect(() => {
        if (!selectedContactId) {
            setSearch('');
        }
    }, [selectedContactId]);

    // Busca no servidor quando a lista local não cobre o termo (volume grande / estado desatualizado).
    useEffect(() => {
        const term = sanitizeContactSearchTerm(search);
        if (!currentOrganization?.id || term.length < 2) {
            setRemoteContacts([]);
            setRemoteLoading(false);
            return;
        }

        let cancelled = false;
        setRemoteLoading(true);
        const handle = window.setTimeout(async () => {
            const pattern = `%${term}%`;
            const { data, error } = await supabase
                .from('contacts')
                .select('id, name, email, phone, whatsapp, company, assigned_to, origins(name)')
                .eq('organization_id', currentOrganization.id)
                .or(
                    `name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern},whatsapp.ilike.${pattern}`
                )
                .order('name')
                .limit(40);

            if (cancelled) return;

            if (error) {
                console.error('[ContactSelect] remote search', error);
                setRemoteContacts([]);
            } else {
                const mapped = (data || []).map((c: any) => ({
                    ...c,
                    origins: (Array.isArray(c.origins) && c.origins.length > 0
                        ? c.origins[0]
                        : c.origins || null) as { name: string } | null
                })) as Contact[];
                setRemoteContacts(mapped);
            }
            setRemoteLoading(false);
        }, 280);

        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [search, currentOrganization?.id]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const filteredContacts = mergedContacts.filter(contact =>
        contact.name.toLowerCase().includes(search.toLowerCase()) ||
        contact.email?.toLowerCase().includes(search.toLowerCase()) ||
        contact.whatsapp?.includes(search) ||
        contact.phone?.includes(search)
    );

    const handleSearch = (value: string) => {
        setSearch(value);
        setIsOpen(true);
        if (!value) {
            onClear();
        }
    };

    const handleSelect = (contact: Contact) => {
        onSelect(contact);
        setSearch(contact.name);
        setIsOpen(false);
    };

    return (
        <div className="flex gap-2" ref={dropdownRef}>
            <div className="flex-1 relative">
                <SearchInput
                    value={search}
                    onChange={handleSearch}
                    placeholder="Pesquisar contato..."
                    className="w-full"
                    onFocus={() => setIsOpen(true)}
                />

                {/* Dropdown de resultados */}
                {isOpen && search && !selectedContactId && (
                    <div className="absolute z-10 mt-s1 max-h-60 w-full overflow-y-auto rounded-lg border border-subtle bg-surface-0 shadow-lg">
                        {filteredContacts.length > 0 ? (
                            <>
                                {filteredContacts.map(contact => (
                                    <button
                                        key={contact.id}
                                        type="button"
                                        onClick={() => handleSelect(contact)}
                                        className="w-full px-4 py-3 text-left hover:bg-accent-primary-subtle transition-colors border-b border-subtle last:border-b-0"
                                    >
                                        <div className="font-medium text-default">{contact.name}</div>
                                        {contact.email && (
                                            <div className="text-xs text-muted mt-0.5">{contact.email}</div>
                                        )}
                                        {contact.whatsapp && (
                                            <div className="text-xs text-muted">{contact.whatsapp}</div>
                                        )}
                                    </button>
                                ))}
                                {remoteLoading && (
                                    <div className="px-4 py-2 text-xs text-muted text-center border-t border-subtle">
                                        Atualizando resultados...
                                    </div>
                                )}
                            </>
                        ) : remoteLoading ? (
                            <div className="px-4 py-3 text-sm text-muted text-center">
                                Buscando contatos...
                            </div>
                        ) : (
                            <div className="px-4 py-3 text-sm text-muted text-center">
                                Nenhum contato encontrado
                            </div>
                        )}
                    </div>
                )}

                {isOpen && search && selectedContactId && (
                    <div className="absolute z-10 mt-s1 w-full rounded-lg border border-subtle bg-surface-0 shadow-lg">
                        <button
                            type="button"
                            onClick={() => {
                                onClear();
                                setSearch('');
                                setIsOpen(true);
                            }}
                            className="w-full min-h-11 touch-manipulation px-s4 py-s3 text-left text-sm text-danger-text transition-colors hover:bg-danger-bg md:min-h-0"
                        >
                            Remover seleção
                        </button>
                    </div>
                )}

                {/* Mensagem de validação visual */}
                {!selectedContactId && search && !isOpen && (
                    <p className="mt-s1 text-xs text-muted">
                        Selecione um contato da lista
                    </p>
                )}
            </div>
            <button
                type="button"
                onClick={onAddContact}
                className="flex size-11 shrink-0 touch-manipulation items-center justify-center rounded-lg bg-primary text-primary-foreground transition-colors hover:bg-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                title="Adicionar novo contato"
                aria-label="Adicionar novo contato"
            >
                <PlusIcon className="w-5 h-5" />
            </button>
        </div>
    );
};

export default ContactSelect;
